import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { MyHead } from '@/components/molecules/MyHead'
import { JobPageSidebar } from '@/components/organisms/JobPageSidebar'
import { JobPage } from '@/components/organisms/JobPage'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { ProfileTwoColumnContentTemplate } from '@/components/templates/ProfileTwoColumnContentTemplate'
import { IJobBase, Language } from '@/types'
import { getJobThumbnail, getStaticStores } from '@/utils'
import { JobApplicationModal } from '@/components/molecules/JobApplicationModal'
import { SubHeading1 } from '@/components/atoms/SubHeading1'
import { PrimaryRecruitingContactItem } from '@/components/molecules/PrimaryRecruitingContactItem'
import { ProfileCompletionPromotionPopup } from '@/components/organisms/ProfileCompletionPromotionPopup'

type Props = {
  job: IJobBase
}

const Job: NextPage<Props> = ({ job }) => {
  const { t } = useTranslation(['p'])
  const title = t('の求人情報 -', { name: job?.company?.name, title: job?.title })
  const description = t(
    'の求人情報です。AQA はスタートアップ・ベンチャー企業の転職・副業・インターン等の求人サイトです。',
    {
      name: job?.company?.name,
      title: job?.title,
    }
  )
  const image = getJobThumbnail(job)
  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead title={title} description={description} image={image} isTitleWithSuffix={false} />
      <ProfileTwoColumnContentTemplate
        aside={
          <JobPageSidebar
            company={job?.company}
            renderHeading={() => <SubHeading1>{t('会社情報')}</SubHeading1>}
            renderFooter={() => <PrimaryRecruitingContactItem name={t('採用担当者')} job={job} />}
          />
        }
      >
        <JobPage job={job} />
        <JobApplicationModal job={job} />
        <ProfileCompletionPromotionPopup type='jobSeeker' />
      </ProfileTwoColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const { viewer, jobs } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const jobSlug = params.job_slug as string
  const job = await jobs.fetchJob({ slug: jobSlug })

  if (!job) {
    return {
      notFound: true,
      revalidate: 1,
    }
  }

  return {
    props: {
      job,
      ...(await serverSideTranslations(locale, [
        'global',
        'companies-users-[id]',
        'companies-[id]',
        'jobs-[job_slug]',
        'jobs',
        'p',
        'validation',
      ])),
    },
    revalidate: 1,
  }
}

// eslint-disable-next-line @typescript-eslint/require-await
export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default Job
